<template>
  <UModal
    :title="validationResult?.fileName ?? ''"
    :description="t('sapImport.validation.modalTitle')"
    class="max-w-[60rem] w-[90vw]"
    v-model:open="isOpen"
    :dismissible="true"
    :ui="{
      overlay: 'bg-gray-950/75 dark:bg-gray-950/90 z-[60]',
      content: 'z-[60]',
      header: 'flex items-center justify-between p-3 sm:px-4 min-h-12 bg-[#e9edf3] dark:bg-(--ui-bg-elevated) border-b border-(--ui-border)',
      body: 'p-0 sm:p-0',
    }"
  >
    <template #header>
      <VisuallyHidden>
        <DialogTitle>{{ validationResult?.fileName }}</DialogTitle>
        <DialogDescription>{{ t('sapImport.validation.modalTitle') }}</DialogDescription>
      </VisuallyHidden>
      
      <div class="flex items-center gap-2 min-w-0">
        <UIcon name="i-lucide-file-warning" class="text-red-500 shrink-0 w-5 h-5" />
        <span class="font-semibold truncate text-base">{{ validationResult?.fileName }}</span>
      </div>
      
      <div class="flex items-center gap-2">
        <UButton
          color="gray"
          variant="ghost"
          icon="i-heroicons-x-mark"
          class="cursor-pointer"
          tabindex="-1"
          @click="isOpen = false"
        />
      </div>
    </template>
    
    <template #body>
      <div class="overflow-auto max-h-[70vh]">
        <UTable
          :columns="columns"
          :data="validationResult?.errors ?? []"
          class="w-full"
          :ui="{
            thead: '[&>tr]:bg-(--ui-bg-elevated)/50 [&>tr]:after:content-none',
            th: 'py-2 px-4',
            td: 'px-4 py-2'
          }"
        />
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { DialogTitle, DialogDescription, VisuallyHidden } from 'reka-ui'
import type { ValidationResult } from '@/composables/useSapValidation'

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  validationResult: ValidationResult | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const columns = computed(() => [
  {
    id: 'field',
    header: t('sapImport.validation.field'),
    accessorKey: 'field'
  },
  {
    id: 'errorCode',
    header: t('sapImport.validation.errorCode'),
    accessorKey: 'errorCode'
  },
  {
    id: 'errorMessage',
    header: t('sapImport.validation.errorMessage'),
    accessorKey: 'errorMessage'
  }
])
</script>
